import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useRef, useState, type FormEvent, type ReactElement } from 'react';
import {
  BITRIX_LIMITS,
  type BitrixConnection,
  type BitrixOptionsResponse,
  type BitrixSettings,
} from '@chatapp-inbox/shared';
import { deleteBitrixConnection, saveBitrixSettings, testBitrix } from '../../api';
import { Button } from '../details/Button';
import { StatusRegion } from '../details/StatusRegion';
import { useStatusMessage } from '../details/use-status-message';
import { bitrixErrorMessage, SETTINGS_SAVED_TEXT } from './bitrix-text';
import { Field, SelectField } from './Field';
import { bitrixKey } from './keys';
import { applyBitrix, useBitrixOpenlines, useBitrixOptions } from './use-bitrix-admin';
import { Switch } from './Switch';
import forms from './forms.module.css';
import section from './Section.module.css';
import styles from './BitrixSection.module.css';

const DAYS_RULE = `Use um número inteiro de 0 a ${BITRIX_LIMITS.activeDaysMax}.`;

/** As opções do Bitrix mais, se o valor guardado não está mais lá (apagado no Bitrix), ele mesmo marcado. */
function withCurrent(
  options: readonly { id: string; name: string }[],
  current: string | null,
): { id: string; name: string }[] {
  if (current && !options.some((o) => o.id === current)) {
    return [...options, { id: current, name: `${current} (não encontrado no Bitrix)` }];
  }
  return [...options];
}

/**
 * Opções da integração, depois de conectado: ligar/desligar, o responsável pelos Leads novos, a etapa
 * inicial, a origem e os dias de atividade. As escolhas vêm do próprio Bitrix. Também testa o acesso e
 * desconecta.
 */
export function BitrixSettingsSection({
  connection,
}: {
  connection: BitrixConnection;
}): ReactElement {
  const queryClient = useQueryClient();
  const headingId = useId();
  const hintId = useId();
  const status = useStatusMessage();
  const options = useBitrixOptions(connection.status === 'connected');
  // Com o Canal Aberto ligado o Bitrix cria o Lead pela linha aberta: a fila, a origem e as regras são da linha.
  const nativeLeads = useBitrixOpenlines().data?.enabled === true;
  const saved = connection.settings;
  const [enabled, setEnabled] = useState(saved.enabled);
  const [assignedUserId, setAssignedUserId] = useState(saved.assignedUserId ?? '');
  const [stageId, setStageId] = useState(saved.stageId ?? '');
  const [sourceId, setSourceId] = useState(saved.sourceId ?? '');
  const [days, setDays] = useState(String(saved.activeDays));
  const [daysError, setDaysError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ ok: boolean; text: string } | null>(null);
  const [confirming, setConfirming] = useState(false);
  const daysRef = useRef<HTMLInputElement>(null);

  const save = useMutation({
    mutationFn: saveBitrixSettings,
    onSuccess: (next) => applyBitrix(queryClient, next),
  });
  const test = useMutation({ mutationFn: testBitrix });
  const disconnect = useMutation({
    mutationFn: deleteBitrixConnection,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: bitrixKey });
    },
  });

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (save.isPending) return;
    setFormError(null);
    const parsed = /^\d{1,4}$/.test(days.trim()) ? Number(days.trim()) : NaN;
    if (!Number.isInteger(parsed) || parsed > BITRIX_LIMITS.activeDaysMax) {
      setDaysError(DAYS_RULE);
      daysRef.current?.focus();
      return;
    }
    setDaysError(null);
    const body: BitrixSettings = {
      enabled,
      assignedUserId: assignedUserId || null,
      stageId: stageId || null,
      sourceId: sourceId || null,
      activeDays: parsed,
    };
    save.mutate(body, {
      onSuccess: () => status.announce(SETTINGS_SAVED_TEXT),
      onError: (error) =>
        setFormError(
          bitrixErrorMessage(error, 'Não foi possível salvar as opções. Tente de novo.'),
        ),
    });
  };

  const runTest = (): void => {
    if (test.isPending) return;
    setTestResult(null);
    test.mutate(undefined, {
      onSuccess: ({ portal }) =>
        setTestResult({ ok: true, text: `Conexão funcionando com ${portal}.` }),
      onError: (error) =>
        setTestResult({
          ok: false,
          text: bitrixErrorMessage(error, 'Não foi possível falar com o Bitrix. Tente de novo.'),
        }),
    });
  };

  const data: BitrixOptionsResponse | undefined = options.data;
  const busy = save.isPending;

  return (
    <section aria-labelledby={headingId} className={section.card}>
      <h3 id={headingId} className={section.cardTitle}>
        Leads
      </h3>
      <p id={hintId} className={forms.hint}>
        Quando o cliente escreve, o CladvApp procura um Lead ativo com o telefone dele no Bitrix. Se
        achar, vincula a conversa a ele; se não achar, cria um Lead novo na etapa inicial, para o
        responsável escolhido abaixo.
      </p>
      {nativeLeads && (
        <p role="note" className={styles.notice}>
          O Canal Aberto está ligado: quem cria o Lead é o Bitrix, pela linha aberta. A fila de
          atendentes, a origem, o que fazer quando o cliente não está no CRM, os horários e os
          avisos são da própria linha, no Bitrix: Contact Center, Canais Abertos, a linha “WhatsApp:
          …”, Configurar. As escolhas abaixo valem só quando o Canal Aberto está desligado e para
          uma linha nova que o CladvApp venha a criar.
        </p>
      )}
      {connection.pending > 0 && (
        <p className={forms.hint}>
          Conferências na fila: {connection.pending}
          {connection.retrying > 0 ? ` (${connection.retrying} esperando para tentar de novo)` : ''}
          .
        </p>
      )}

      {options.isPending && (
        <p role="status" className={forms.hint}>
          Carregando as escolhas do Bitrix…
        </p>
      )}
      {options.isError && (
        <div className={styles.loadError}>
          <p role="alert" className={forms.alert}>
            {bitrixErrorMessage(options.error, 'Não foi possível carregar as escolhas do Bitrix.')}
          </p>
          <Button busy={options.isFetching} onClick={() => void options.refetch()}>
            Tentar de novo
          </Button>
        </div>
      )}

      <form className={forms.form} noValidate aria-busy={busy || undefined} onSubmit={submit}>
        <div className={styles.switchRow}>
          <Switch
            checked={enabled}
            label="Criar e vincular Leads automaticamente"
            busy={busy}
            describedBy={hintId}
            onToggle={() => setEnabled((on) => !on)}
          />
          <span className={styles.switchLabel} aria-hidden="true">
            Criar e vincular Leads automaticamente: {enabled ? 'ligado' : 'desligado'}
          </span>
        </div>

        <SelectField
          label="Responsável pelos Leads novos"
          hint="Sem responsável, o CladvApp só vincula os Leads que já existem; não cria Leads novos."
          value={assignedUserId}
          disabled={busy || !data}
          onChange={(event) => setAssignedUserId(event.target.value)}
        >
          <option value="">Nenhum (só vincular)</option>
          {withCurrent(
            (data?.users ?? []).map((u) => ({
              id: u.id,
              name: u.email ? `${u.name} (${u.email})` : u.name,
            })),
            assignedUserId || null,
          ).map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </SelectField>
        <SelectField
          label="Etapa inicial dos Leads novos"
          value={stageId}
          disabled={busy || !data}
          onChange={(event) => setStageId(event.target.value)}
        >
          <option value="">Primeira etapa do funil</option>
          {withCurrent(data?.stages ?? [], stageId || null).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </SelectField>
        <SelectField
          label="Origem dos Leads novos"
          value={sourceId}
          disabled={busy || !data}
          onChange={(event) => setSourceId(event.target.value)}
        >
          <option value="">Nenhuma</option>
          {withCurrent(data?.sources ?? [], sourceId || null).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </SelectField>
        <Field
          ref={daysRef}
          label="Dias de atividade do Lead"
          hint="Um Lead parado há mais dias que isto deixa de contar como ativo, e a próxima mensagem do cliente cria um novo. Com 0, vale só o status do Bitrix (em andamento, ganho ou perdido)."
          type="number"
          inputMode="numeric"
          min={0}
          max={BITRIX_LIMITS.activeDaysMax}
          value={days}
          readOnly={busy}
          error={daysError}
          onChange={(event) => setDays(event.target.value)}
        />

        {formError && (
          <p role="alert" className={forms.alert}>
            {formError}
          </p>
        )}
        <div className={forms.actions}>
          <Button type="submit" variant="primary" busy={busy}>
            {busy ? 'Salvando…' : 'Salvar opções'}
          </Button>
          <Button busy={test.isPending} onClick={runTest}>
            {test.isPending ? 'Testando…' : 'Testar conexão'}
          </Button>
        </div>
        <StatusRegion message={status.message} />
        {testResult && (
          <p
            role={testResult.ok ? 'status' : 'alert'}
            className={testResult.ok ? forms.hint : forms.alert}
          >
            {testResult.text}
          </p>
        )}
      </form>

      <div className={styles.danger}>
        {confirming ? (
          <>
            <p className={forms.hint}>
              Desconectar apaga a chave e o acesso guardados. As conversas continuam vinculadas aos
              Leads que já têm, mas nada novo é criado até conectar de novo.
            </p>
            <div className={forms.actions}>
              <Button
                variant="danger"
                busy={disconnect.isPending}
                onClick={() => disconnect.mutate()}
              >
                Sim, desconectar
              </Button>
              <Button onClick={() => setConfirming(false)}>Cancelar</Button>
            </div>
            {disconnect.isError && (
              <p role="alert" className={forms.alert}>
                {bitrixErrorMessage(
                  disconnect.error,
                  'Não foi possível desconectar. Tente de novo.',
                )}
              </p>
            )}
          </>
        ) : (
          <Button variant="dangerOutline" onClick={() => setConfirming(true)}>
            Desconectar do Bitrix
          </Button>
        )}
      </div>
    </section>
  );
}
